import React, { useState } from 'react';
import styled from 'styled-components';
import { FaBookmark, FaSave, FaStar, FaTrash } from 'react-icons/fa';

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const Select = styled.select`
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
  font-size: 0.85rem;
  color: var(--color-text-primary);
  min-width: 180px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : 'var(--color-border-primary)')};
  background: ${({ $primary }) => ($primary ? '#28a745' : 'var(--color-bg-primary)')};
  color: ${({ $primary }) => ($primary ? '#fff' : 'var(--color-text-primary)')};
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--color-bg-primary);
  border-radius: 14px;
  padding: 1.25rem;
  width: min(92vw, 380px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1rem;
  color: var(--color-text-primary);
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  margin-bottom: 0.75rem;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin-bottom: 1rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const FilterPresetToolbar = ({
  presets = [],
  activePresetId = '',
  onSelectPreset,
  onSavePreset,
  onSetDefault,
  onDeletePreset,
  disabled = false,
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [markAsDefault, setMarkAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!presetName.trim()) return;
    setSaving(true);
    try {
      await onSavePreset({ name: presetName.trim(), defaultPreset: markAsDefault });
      setShowSaveModal(false);
      setPresetName('');
      setMarkAsDefault(false);
    } finally {
      setSaving(false);
    }
  };

  const activePreset = presets.find((p) => p.id === activePresetId);

  return (
    <>
      <Toolbar>
        <FaBookmark color="var(--color-text-secondary)" />
        <Select
          value={activePresetId}
          onChange={(e) => onSelectPreset(e.target.value)}
          disabled={disabled}
        >
          <option value="">Filtros salvos…</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
              {preset.defaultPreset ? ' ★' : ''}
            </option>
          ))}
        </Select>
        <Btn type="button" $primary onClick={() => setShowSaveModal(true)} disabled={disabled}>
          <FaSave />
          Salvar filtros
        </Btn>
        {activePreset && !activePreset.defaultPreset && (
          <Btn type="button" onClick={() => onSetDefault(activePreset.id)} disabled={disabled}>
            <FaStar />
            Tornar padrão
          </Btn>
        )}
        {activePreset && (
          <Btn type="button" onClick={() => onDeletePreset(activePreset.id)} disabled={disabled}>
            <FaTrash />
            Excluir
          </Btn>
        )}
      </Toolbar>

      {showSaveModal && (
        <ModalBackdrop onClick={() => !saving && setShowSaveModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Salvar filtros atuais</ModalTitle>
            <Input
              placeholder="Nome do filtro (ex.: Minhas melhorias)"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              autoFocus
            />
            <CheckboxRow>
              <input
                type="checkbox"
                checked={markAsDefault}
                onChange={(e) => setMarkAsDefault(e.target.checked)}
              />
              Usar como padrão nesta vista
            </CheckboxRow>
            <ModalActions>
              <Btn type="button" onClick={() => setShowSaveModal(false)} disabled={saving}>
                Cancelar
              </Btn>
              <Btn type="button" $primary onClick={handleSave} disabled={saving || !presetName.trim()}>
                Salvar
              </Btn>
            </ModalActions>
          </Modal>
        </ModalBackdrop>
      )}
    </>
  );
};

export default FilterPresetToolbar;
